import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getProfile } from '../services/profileService';
import { getMemberLabData, getHistory } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';
import RbcFormula from '../formula/Rbc';
import HemoglobinFormula from '../formula/Hemoglobin';
import HematocritFormula from '../formula/Hematocrit';
import WbcFormula from '../formula/Wbc';
import PlateletFormula from '../formula/Platelet';
import McvFormula from '../formula/Mcv';
import MchFormula from '../formula/Mch';
import MchcFormula from '../formula/Mchc';

const CBCContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #999;
  font-weight: normal;
  margin-top: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#4CAF50'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CBCIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ResultCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 0;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  background: #00BBD3;
  color: white;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CardBody = styled.div`
  padding: 16px;
`;

const DateRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const DateLabel = styled.div`
  font-size: 14px;
  color: #999;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const DateValue = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const HospitalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const HospitalLabel = styled.div`
  font-size: 14px;
  color: #999;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const HospitalValue = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const TestResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TestResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const TestLabel = styled.div`
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const TestValue = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #4CAF50;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const CBCPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cbcRecords, setCbcRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [latestDate, setLatestDate] = useState('');
  const [profileData, setProfileData] = useState({ age: 0, gender: '' });
  const [latestValues, setLatestValues] = useState({
    rbc: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    hemoglobin: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    hematocrit: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    wbc: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    platelet: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    mcv: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    mch: { value: '-', status: 'ปกติ', color: '#4CAF50' },
    mchc: { value: '-', status: 'ปกติ', color: '#4CAF50' }
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    wbc: '',
    rbc: '',
    hemoglobin: '',
    hematocrit: '',
    platelet: '',
    mcv: '',
    mch: '',
    mchc: ''
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      wbc: record.wbc || '',
      rbc: record.rbc || '',
      hemoglobin: record.hemoglobin || '',
      hematocrit: record.hematocrit || '',
      platelet: record.platelet || '',
      mcv: record.mcv || '',
      mch: record.mch || '',
      mchc: record.mchc || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      console.log('📝 Updating CBC data:', editingRecord.date, editForm);

      const updates = [];

      if (editForm.wbc && editForm.wbc !== editingRecord.wbc) {
        updates.push(updateLabData(editingRecord.date, 'wbc', editForm.wbc));
      }

      if (editForm.rbc && editForm.rbc !== editingRecord.rbc) {
        updates.push(updateLabData(editingRecord.date, 'rbc', editForm.rbc));
      }

      if (editForm.hemoglobin && editForm.hemoglobin !== editingRecord.hemoglobin) {
        updates.push(updateLabData(editingRecord.date, 'hemoglobin', editForm.hemoglobin));
      }

      if (editForm.hematocrit && editForm.hematocrit !== editingRecord.hematocrit) {
        updates.push(updateLabData(editingRecord.date, 'hematocrit', editForm.hematocrit));
      }

      if (editForm.platelet && editForm.platelet !== editingRecord.platelet) {
        updates.push(updateLabData(editingRecord.date, 'platelet', editForm.platelet));
      }

      if (editForm.mcv && editForm.mcv !== editingRecord.mcv) {
        updates.push(updateLabData(editingRecord.date, 'mcv', editForm.mcv));
      }

      if (editForm.mch && editForm.mch !== editingRecord.mch) {
        updates.push(updateLabData(editingRecord.date, 'mch', editForm.mch));
      }

      if (editForm.mchc && editForm.mchc !== editingRecord.mchc) {
        updates.push(updateLabData(editingRecord.date, 'mchc', editForm.mchc));
      }

      await Promise.all(updates);

      console.log('✅ CBC data updated successfully');
      alert('อัปเดตข้อมูลเรียบร้อยแล้ว');

      await loadCBCData();

      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      console.error('❌ Error updating CBC data:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const [datePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${(parseInt(year) + 543).toString()}`;
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '';

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const datePart = dateString.split(' ')[0];
    const [year, month, day] = datePart.split('-');

    const thaiYear = parseInt(year) + 543;
    const thaiMonth = thaiMonths[parseInt(month) - 1];

    return `${parseInt(day)} ${thaiMonth} ${thaiYear}`;
  };

  const loadCBCData = async () => {
    try {
      setLoading(true);
      console.log('🩺 CBC - Loading data...');

      // ====== ดึงข้อมูลจาก MFU API (เส้นเดียวกับ mfu-admin) ======
      let memberLabData = null;
      let age = 0;
      let gender = 'M';

      try {
        memberLabData = await getMemberLabData();
        console.log('📊 Member Lab Data:', memberLabData);

        if (memberLabData?.profile) {
          age = memberLabData.profile.age || 0;
          gender = memberLabData.profile.gender || 'M';
        }
      } catch (memberLabError) {
        console.warn("⚠️ Could not load member lab data:", memberLabError);
      }

      // Fallback: ถ้าไม่ได้ profile จาก memberLabData
      if (!age) {
        const profileResponse = await getProfile();
        const profile = profileResponse?.data || profileResponse?.profile || {};
        age = profile.age || 0;
        gender = profile.gender || 'M';
      }

      setProfileData({ age, gender });

      const labData = memberLabData?.labData || {};

      // ดึงข้อมูลจาก memberLabData หรือ fallback ไป API เดิม
      let wbcArray = [];
      let rbcArray = [];
      let hemoglobinArray = [];
      let hematocritArray = [];
      let plateletArray = [];
      let mcvArray = [];
      let mchArray = [];
      let mchcArray = [];
      let neutrophileArray = [];
      let lymphocyteArray = [];
      let monocyteArray = [];
      let basophilArray = [];

      // ลองดึงจาก memberLabData ก่อน
      const wbcHistory = getHistory(labData, 'wbc');
      const rbcHistory = getHistory(labData, 'rbc');
      const hemoglobinHistory = getHistory(labData, 'hemoglobin');
      const hematocritHistory = getHistory(labData, 'hematocrit');
      const plateletHistory = getHistory(labData, 'platelet');
      const mcvHistory = getHistory(labData, 'mcv');
      const mchHistory = getHistory(labData, 'mch');
      const mchcHistory = getHistory(labData, 'mchc');
      const neutrophileHistory = getHistory(labData, 'neutrophile');
      const lymphocyteHistory = getHistory(labData, 'lymphocyte-count');
      const monocyteHistory = getHistory(labData, 'monocyte-count');
      const basophilHistory = getHistory(labData, 'basophil-count');

      if (wbcHistory.length > 0 || rbcHistory.length > 0 || hemoglobinHistory.length > 0) {
        // แปลงรูปแบบข้อมูลให้ตรงกับที่ใช้
        wbcArray = wbcHistory.map(item => ({ dateadd: item.date, value: item.value }));
        rbcArray = rbcHistory.map(item => ({ dateadd: item.date, value: item.value }));
        hemoglobinArray = hemoglobinHistory.map(item => ({ dateadd: item.date, value: item.value }));
        hematocritArray = hematocritHistory.map(item => ({ dateadd: item.date, value: item.value }));
        plateletArray = plateletHistory.map(item => ({ dateadd: item.date, value: item.value }));
        mcvArray = mcvHistory.map(item => ({ dateadd: item.date, value: item.value }));
        mchArray = mchHistory.map(item => ({ dateadd: item.date, value: item.value }));
        mchcArray = mchcHistory.map(item => ({ dateadd: item.date, value: item.value }));
        neutrophileArray = neutrophileHistory.map(item => ({ dateadd: item.date, value: item.value }));
        lymphocyteArray = lymphocyteHistory.map(item => ({ dateadd: item.date, value: item.value }));
        monocyteArray = monocyteHistory.map(item => ({ dateadd: item.date, value: item.value }));
        basophilArray = basophilHistory.map(item => ({ dateadd: item.date, value: item.value }));
      } else {
        // Fallback ไป API เดิม
        const [wbcResponse, rbcResponse, hemoglobinResponse, hematocritResponse, plateletResponse, mcvResponse, mchResponse, mchcResponse] = await Promise.all([
          getLabDataByName('wbc'),
          getLabDataByName('rbc'),
          getLabDataByName('hemoglobin'),
          getLabDataByName('hematocrit'),
          getLabDataByName('platelet'),
          getLabDataByName('mcv'),
          getLabDataByName('mch'),
          getLabDataByName('mchc')
        ]);

        wbcArray = wbcResponse?.data || [];
        rbcArray = rbcResponse?.data || [];
        hemoglobinArray = hemoglobinResponse?.data || [];
        hematocritArray = hematocritResponse?.data || [];
        plateletArray = plateletResponse?.data || [];
        mcvArray = mcvResponse?.data || [];
        mchArray = mchResponse?.data || [];
        mchcArray = mchcResponse?.data || [];
      }

      console.log('📊 CBC Data:', {
        WBC: wbcArray.length,
        RBC: rbcArray.length,
        Hemoglobin: hemoglobinArray.length,
        Hematocrit: hematocritArray.length,
        Platelet: plateletArray.length,
        MCV: mcvArray.length,
        MCH: mchArray.length,
        MCHC: mchcArray.length
      });

      // Combine all records by date
      const recordsMap = new Map();

      wbcArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).wbc = item.value;
      });

      rbcArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).rbc = item.value;
      });

      hemoglobinArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).hemoglobin = item.value;
      });

      hematocritArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).hematocrit = item.value;
      });

      plateletArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).platelet = item.value;
      });

      mcvArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).mcv = item.value;
      });

      mchArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).mch = item.value;
      });

      mchcArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).mchc = item.value;
      });

      neutrophileArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).neutrophile = item.value;
      });

      lymphocyteArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).lymphocyte = item.value;
      });

      monocyteArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).monocyte = item.value;
      });

      basophilArray.forEach(item => {
        const date = item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, hospital: item.hospital || 'โรงพยาบาลมหาวิทยาลัยแม่ฟ้าหลวง' });
        }
        recordsMap.get(date).basophil = item.value;
      });

      const combinedRecords = Array.from(recordsMap.values()).sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );

      // แสดงแค่ 2 การ์ดล่าสุด
      setCbcRecords(combinedRecords.slice(0, 2));

      // เก็บทั้งหมดไว้สำหรับตาราง
      setAllRecords(combinedRecords);

      // Set latest date
      if (combinedRecords.length > 0) {
        setLatestDate(formatThaiDate(combinedRecords[0].date));

        // Calculate status for latest values using formulas
        const latest = combinedRecords[0];
        const colorMap = { 'green': '#4CAF50', 'red': '#ff4757', 'gray': '#e0e0e0' };

        const rbcValue = parseFloat(latest.rbc || 0);
        const rbcResult = latest.rbc ? await RbcFormula(rbcValue, age, gender) : null;

        const hemoglobinValue = parseFloat(latest.hemoglobin || 0);
        const hemoglobinResult = latest.hemoglobin ? await HemoglobinFormula(hemoglobinValue, age, gender) : null;

        const hematocritValue = parseFloat(latest.hematocrit || 0);
        const hematocritResult = latest.hematocrit ? await HematocritFormula(hematocritValue, age, gender) : null;

        const wbcValue = parseFloat(latest.wbc || 0);
        const wbcResult = latest.wbc ? await WbcFormula(wbcValue, age, gender) : null;

        const plateletValue = parseFloat(latest.platelet || 0);
        const plateletResult = latest.platelet ? await PlateletFormula(plateletValue, age, gender) : null;

        const mcvValue = parseFloat(latest.mcv || 0);
        const mcvResult = latest.mcv ? await McvFormula(mcvValue, age, gender) : null;

        const mchValue = parseFloat(latest.mch || 0);
        const mchResult = latest.mch ? await MchFormula(mchValue, age, gender) : null;

        const mchcValue = parseFloat(latest.mchc || 0);
        const mchcResult = latest.mchc ? await MchcFormula(mchcValue, age, gender) : null;

        setLatestValues({
          rbc: {
            value: latest.rbc ? `${rbcValue.toFixed(2)} x10⁶/µL` : '-',
            status: rbcResult?.title || 'ปกติ',
            color: rbcResult ? (colorMap[rbcResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: rbcValue
          },
          hemoglobin: {
            value: latest.hemoglobin ? `${hemoglobinValue.toFixed(1)} g/dL` : '-',
            status: hemoglobinResult?.title || 'ปกติ',
            color: hemoglobinResult ? (colorMap[hemoglobinResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: hemoglobinValue
          },
          hematocrit: {
            value: latest.hematocrit ? `${hematocritValue.toFixed(1)} %` : '-',
            status: hematocritResult?.title || 'ปกติ',
            color: hematocritResult ? (colorMap[hematocritResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: hematocritValue
          },
          wbc: {
            value: latest.wbc ? `${wbcValue.toFixed(1)} x10³/µL` : '-',
            status: wbcResult?.title || 'ปกติ',
            color: wbcResult ? (colorMap[wbcResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: wbcValue
          },
          platelet: {
            value: latest.platelet ? `${plateletValue.toFixed(0)} x10³/µL` : '-',
            status: plateletResult?.title || 'ปกติ',
            color: plateletResult ? (colorMap[plateletResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: plateletValue
          },
          mcv: {
            value: latest.mcv ? `${mcvValue.toFixed(1)} fL` : '-',
            status: mcvResult?.title || 'ปกติ',
            color: mcvResult ? (colorMap[mcvResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: mcvValue
          },
          mch: {
            value: latest.mch ? `${mchValue.toFixed(1)} pg` : '-',
            status: mchResult?.title || 'ปกติ',
            color: mchResult ? (colorMap[mchResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: mchValue
          },
          mchc: {
            value: latest.mchc ? `${mchcValue.toFixed(1)} g/dL` : '-',
            status: mchcResult?.title || 'ปกติ',
            color: mchcResult ? (colorMap[mchcResult.color] || '#4CAF50') : '#4CAF50',
            rawValue: mchcValue
          }
        });
      }

      console.log('✅ CBC - Data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading CBC data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCBCData();
  }, []);

  return (
    <CBCContainer>
      <MainHeader />

      <TopBar>ความสมบูรณ์ของเลือด</TopBar>

      <Header>
        <BackButton onClick={handleBack}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ความสมบูรณ์ของเลือด (CBC)</PageTitle>
        {latestDate && <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : allRecords.length === 0 ? (
          <EmptyState>ไม่พบข้อมูลการตรวจ</EmptyState>
        ) : (
          <>
            {/* Status Card */}
            {(() => {
              // Check for any abnormal values
              const abnormalValues = [];

              if (latestValues.rbc.value !== '-' && latestValues.rbc.color !== '#4CAF50') {
                abnormalValues.push({ name: 'RBC', ...latestValues.rbc });
              }
              if (latestValues.hemoglobin.value !== '-' && latestValues.hemoglobin.color !== '#4CAF50') {
                abnormalValues.push({ name: 'Hemoglobin', ...latestValues.hemoglobin });
              }
              if (latestValues.hematocrit.value !== '-' && latestValues.hematocrit.color !== '#4CAF50') {
                abnormalValues.push({ name: 'Hematocrit', ...latestValues.hematocrit });
              }
              if (latestValues.wbc.value !== '-' && latestValues.wbc.color !== '#4CAF50') {
                abnormalValues.push({ name: 'WBC', ...latestValues.wbc });
              }
              if (latestValues.platelet.value !== '-' && latestValues.platelet.color !== '#4CAF50') {
                abnormalValues.push({ name: 'Platelet', ...latestValues.platelet });
              }
              if (latestValues.mcv.value !== '-' && latestValues.mcv.color !== '#4CAF50') {
                abnormalValues.push({ name: 'MCV', ...latestValues.mcv });
              }
              if (latestValues.mch.value !== '-' && latestValues.mch.color !== '#4CAF50') {
                abnormalValues.push({ name: 'MCH', ...latestValues.mch });
              }
              if (latestValues.mchc.value !== '-' && latestValues.mchc.color !== '#4CAF50') {
                abnormalValues.push({ name: 'MCHC', ...latestValues.mchc });
              }

              // Determine card color (use worst color)
              let cardColor = '#4CAF50'; // default green
              if (abnormalValues.length > 0) {
                const colors = abnormalValues.map(v => v.color);
                // Priority: red > orange > yellow > green
                if (colors.includes('#ff4757')) cardColor = '#ff4757';
                else if (colors.includes('#FF9800')) cardColor = '#FF9800';
                else if (colors.includes('#FBBC04')) cardColor = '#FBBC04';
              }

              return (
                <StatusCard $bgColor={cardColor}>
                  <CBCIcon src="./ความสมบูรณ์ของเลือดสี.png" alt="ความสมบูรณ์ของเลือด" />
                  <StatusInfo>
                    {abnormalValues.length === 0 ? (
                      <StatusTitle style={{ color: 'white' }}>ผลตรวจปกติ</StatusTitle>
                    ) : (
                      <>
                        {abnormalValues.map((item, index) => (
                          <AbnormalItem key={index}>
                            <AbnormalName>{item.name}</AbnormalName>
                            <AbnormalValue>{item.value}</AbnormalValue>
                            <AbnormalStatus>{item.status}</AbnormalStatus>
                          </AbnormalItem>
                        ))}
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()}

            {/* แสดง 2 การ์ดล่าสุด */}
            {cbcRecords.map((record, index) => (
              <ResultCard key={index}>
                <CardHeader>ผลตรวจ</CardHeader>
                <CardBody>
                  <DateRow>
                    <DateLabel>วันที่ตรวจ</DateLabel>
                    <DateValue>{formatDisplayDate(record.date)}</DateValue>
                  </DateRow>
                  <HospitalRow>
                    <HospitalLabel>โรงพยาบาล</HospitalLabel>
                    <HospitalValue>{record.hospital}</HospitalValue>
                  </HospitalRow>
                  <TestResultsSection>
                    {record.rbc && (
                      <TestResultRow>
                        <TestLabel>RBC (Red Blood Cell)</TestLabel>
                        <TestValue>{record.rbc} x10⁶/µL</TestValue>
                      </TestResultRow>
                    )}
                    {record.hemoglobin && (
                      <TestResultRow>
                        <TestLabel>Hemoglobin</TestLabel>
                        <TestValue>{record.hemoglobin} g/dL</TestValue>
                      </TestResultRow>
                    )}
                    {record.hematocrit && (
                      <TestResultRow>
                        <TestLabel>Hematocrit</TestLabel>
                        <TestValue>{record.hematocrit} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.wbc && (
                      <TestResultRow>
                        <TestLabel>WBC (White Blood Cell)</TestLabel>
                        <TestValue>{record.wbc} x10³/µL</TestValue>
                      </TestResultRow>
                    )}
                    {record.platelet && (
                      <TestResultRow>
                        <TestLabel>Platelet</TestLabel>
                        <TestValue>{record.platelet} x10³/µL</TestValue>
                      </TestResultRow>
                    )}
                    {record.mcv && (
                      <TestResultRow>
                        <TestLabel>MCV</TestLabel>
                        <TestValue>{record.mcv} fL</TestValue>
                      </TestResultRow>
                    )}
                    {record.mch && (
                      <TestResultRow>
                        <TestLabel>MCH</TestLabel>
                        <TestValue>{record.mch} pg</TestValue>
                      </TestResultRow>
                    )}
                    {record.mchc && (
                      <TestResultRow>
                        <TestLabel>MCHC</TestLabel>
                        <TestValue>{record.mchc} g/dL</TestValue>
                      </TestResultRow>
                    )}
                    {record.neutrophile && (
                      <TestResultRow>
                        <TestLabel>Neutrophile</TestLabel>
                        <TestValue>{record.neutrophile} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.lymphocyte && (
                      <TestResultRow>
                        <TestLabel>Lymphocyte</TestLabel>
                        <TestValue>{record.lymphocyte} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.monocyte && (
                      <TestResultRow>
                        <TestLabel>Monocyte</TestLabel>
                        <TestValue>{record.monocyte} %</TestValue>
                      </TestResultRow>
                    )}
                    {record.basophil && (
                      <TestResultRow>
                        <TestLabel>Basophil</TestLabel>
                        <TestValue>{record.basophil} %</TestValue>
                      </TestResultRow>
                    )}
                  </TestResultsSection>
                </CardBody>
              </ResultCard>
            ))}

            {/* ตารางประวัติทั้งหมด */}
            <HistorySection>
              <HistoryTitle>ประวัติการตรวจทั้งหมด</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.map((record, index) => {
                    const values = [];
                    if (record.wbc) values.push(`WBC: ${record.wbc}`);
                    if (record.rbc) values.push(`RBC: ${record.rbc}`);
                    if (record.hemoglobin) values.push(`Hb: ${record.hemoglobin}`);
                    if (record.hematocrit) values.push(`Hct: ${record.hematocrit}`);
                    if (record.platelet) values.push(`PLT: ${record.platelet}`);
                    if (record.mcv) values.push(`MCV: ${record.mcv}`);
                    if (record.mch) values.push(`MCH: ${record.mch}`);
                    if (record.mchc) values.push(`MCHC: ${record.mchc}`);
                    if (record.neutrophile) values.push(`Neutrophile: ${record.neutrophile}%`);
                    if (record.lymphocyte) values.push(`Lymphocyte: ${record.lymphocyte}%`);
                    if (record.monocyte) values.push(`Monocyte: ${record.monocyte}%`);
                    if (record.basophil) values.push(`Basophil: ${record.basophil}%`);

                    return (
                      <TableRow key={index}>
                        <TableCell>{formatDisplayDate(record.date)}</TableCell>
                        <TableCell>{values.join(', ') || '-'}</TableCell>
                        <TableCell>
                          <EditButton onClick={() => handleEditClick(record)}>
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </EditButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {/* Edit Modal */}
      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>WBC (x10³/µL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.wbc}
                onChange={(e) => setEditForm({ ...editForm, wbc: e.target.value })}
                placeholder="4.5 - 11.0"
              />
            </FormGroup>

            <FormGroup>
              <Label>RBC (x10⁶/µL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.rbc}
                onChange={(e) => setEditForm({ ...editForm, rbc: e.target.value })}
                placeholder="4.0 - 5.5"
              />
            </FormGroup>

            <FormGroup>
              <Label>Hemoglobin (g/dL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.hemoglobin}
                onChange={(e) => setEditForm({ ...editForm, hemoglobin: e.target.value })}
                placeholder="12 - 17"
              />
            </FormGroup>

            <FormGroup>
              <Label>Hematocrit (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.hematocrit}
                onChange={(e) => setEditForm({ ...editForm, hematocrit: e.target.value })}
                placeholder="36 - 50"
              />
            </FormGroup>

            <FormGroup>
              <Label>Platelet (x10³/µL)</Label>
              <Input
                type="number"
                step="1"
                value={editForm.platelet}
                onChange={(e) => setEditForm({ ...editForm, platelet: e.target.value })}
                placeholder="150 - 450"
              />
            </FormGroup>

            <FormGroup>
              <Label>MCV (fL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.mcv}
                onChange={(e) => setEditForm({ ...editForm, mcv: e.target.value })}
                placeholder="80 - 100"
              />
            </FormGroup>

            <FormGroup>
              <Label>MCH (pg)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.mch}
                onChange={(e) => setEditForm({ ...editForm, mch: e.target.value })}
                placeholder="27 - 31"
              />
            </FormGroup>

            <FormGroup>
              <Label>MCHC (g/dL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.mchc}
                onChange={(e) => setEditForm({ ...editForm, mchc: e.target.value })}
                placeholder="32 - 36"
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </CBCContainer>
  );
};

export default CBCPage;
